import { useQuery } from '@tanstack/react-query';
import { memo, type FC, type HTMLAttributes, type PropsWithChildren } from 'react';
import { useLoaderData } from 'react-router';

import PageSpinner from '../components/molecules/pageSpinner';
import ErrorPage from '../components/pages/errorPage';
import { companyAdmin } from '../constants/appConstants';
import { selectedTeamIdQuery, teamQuery, teamsQuery } from '../queries/teamQuery';
import { userQuery } from '../queries/userQuery';
import { isCompanyAdmin, isSupportAdmin } from '../selectors/userSelectors';
import { useZustand } from '../zustandStore';

import type { rootLoader } from './loaders/rootLoader';

type ProtectedRouteProps = HTMLAttributes<HTMLElement> & { allowedRoles?: string[] };

const ProtectedRoute: FC<PropsWithChildren<ProtectedRouteProps>> = ({ children, allowedRoles }) => {
	const { preventShowErrorPage } = useZustand(state => state.error);
	const initialData = useLoaderData() as Awaited<ReturnType<typeof rootLoader>>;
	const { data: teams, isLoading: isGetTeamsLoading } = useQuery({
		...teamsQuery({ state: 'active' }),
		...(initialData && 'teams' in initialData && { initialData: initialData.teams }),
	});
	const { data: selectedTeamId } = useQuery({
		...selectedTeamIdQuery('selectedTeamID'),
		...(initialData && 'selectedTeamId' in initialData && { initialData: initialData.selectedTeamId }),
	});

	const { data: currentTeam, isLoading: isGetCurrentTeamLoading } = useQuery(
		teamQuery({ teamId: selectedTeamId?.[0] || teams?.[0]?.id || '' }),
	);

	const {
		data: currentUser,
		isLoading: isUserLoading,
		isFetching: isUserFetching,
		isSuccess: isUserSuccess,
	} = useQuery({
		...userQuery,
		initialData: initialData?.userData,
	});

	if (isUserLoading || isGetCurrentTeamLoading || isGetTeamsLoading) {
		return <PageSpinner isLoading={true} />;
	}

	if (allowedRoles?.includes('*')) {
		return children;
	}

	const selectedCurrentTeam = currentTeam?.id || teams?.[0]?.id || '';
	const userRoles = currentUser?.roles || [];
	const userIsSupportAdmin = isSupportAdmin(userRoles || []);
	const userIsCompanyAdmin = isCompanyAdmin(userRoles || []);

	if ((!isUserLoading || !isUserFetching) && isUserSuccess && currentUser && teams) {
		const hasUserRole = userRoles?.some(role => {
			if (userIsCompanyAdmin) {
				return userRoles.length === 1 ? allowedRoles?.includes(role.roleId) : allowedRoles?.includes(companyAdmin);
			}

			const foundRole = allowedRoles?.find(allowedRole => {
				return allowedRole === role.roleId && role.securityGroupId === selectedCurrentTeam;
			});

			return userIsSupportAdmin || foundRole;
		});

		if (!hasUserRole && !preventShowErrorPage) {
			return <ErrorPage />;
		}

		return children;
	}

	return <ErrorPage />;
};

export default memo(ProtectedRoute);
